import React from 'react';
import { Calendar, TrendingUp } from 'lucide-react';
import type { FYForecast as FYForecastType } from '../../../types/acquisitionAnalysis';

interface FYForecastProps {
  data: FYForecastType;
}

export const FYForecast: React.FC<FYForecastProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value === undefined || value === null) return '';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          Fiscal Year Forecast
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Month
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Prior Year
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Current Year
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Growth
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {data.months.map((month, index) => (
                <tr
                  key={index}
                  className={`hover:bg-slate-700/30 transition-colors ${
                    month.status === 'forecast' ? 'bg-amber-500/5' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-sm font-medium text-white flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    {month.month}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-300">
                    {formatCurrency(month.priorYearActual)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-indigo-400">
                    {formatCurrency(month.currentYear)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        month.status === 'actual'
                          ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                          : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                      }`}
                    >
                      {month.status === 'actual' ? 'Actual' : 'Forecast'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {month.growth != null && (
                      <span className="text-green-400 font-medium">
                        {formatPercent(month.growth)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-900/50 font-bold">
                <td className="px-4 py-3 text-sm text-white">Full Year</td>
                <td className="px-4 py-3 text-sm text-right text-slate-300">
                  {formatCurrency(data.fullYearActual)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-indigo-400">
                  {formatCurrency(data.fullYearForecast)}
                </td>
                <td className="px-4 py-3 text-center">-</td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className="text-green-400 font-bold">
                    {formatPercent(data.forecastGrowth)}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            <h4 className="text-sm font-semibold text-emerald-300">
              Updated Valuation (5x Multiple)
            </h4>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Prior Year Basis:</span>
              <span className="text-white font-semibold">
                {formatCurrency(data.valuationUpdate.multiplier5x.priorYear)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Forecast Basis:</span>
              <span className="text-emerald-400 font-bold">
                {formatCurrency(data.valuationUpdate.multiplier5x.forecast)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-indigo-400" />
            <h4 className="text-sm font-semibold text-indigo-300">
              Updated Valuation (6x Multiple)
            </h4>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Prior Year Basis:</span>
              <span className="text-white font-semibold">
                {formatCurrency(data.valuationUpdate.multiplier6x.priorYear)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Forecast Basis:</span>
              <span className="text-indigo-400 font-bold">
                {formatCurrency(data.valuationUpdate.multiplier6x.forecast)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <p className="text-sm text-amber-300">
          <strong>Forecast Methodology:</strong> Remaining months projected using{' '}
          {data.forecastGrowth}% growth assumption{' '}
          {data.actualYtdGrowth !== undefined && data.actualYtdGrowth !== 0 && (
            <>
              (conservative estimate vs {data.actualYtdGrowth > 0 ? '+' : ''}
              {data.actualYtdGrowth.toFixed(1)}% YTD actual)
            </>
          )}
          {' '}based on historical patterns and current year-to-date performance.
        </p>
      </div>
    </div>
  );
};
